<template>
    <div class="next-card">
        <template v-if="routine">
            <div class="next-header">
                <div v-if="tag" class="next-tag">{{ tag }}</div>
                <div class="next-name">{{ routine.name }}</div>
                <div class="next-meta">
                    <span>
                        <v-icon size="12">mdi-clock-outline</v-icon>
                        ~{{ duration }} {{ $t('common.short_minutes') }}
                    </span>
                    <span>
                        <v-icon size="12">mdi-lightning-bolt-outline</v-icon>
                        {{ exercisesCount }} {{ $tc('common.exercises', exercisesCount).toLowerCase() }}
                    </span>
                </div>
            </div>

            <div class="next-exercises">
                <template v-for="(block, bi) in blocks">
                    <div :key="`bl-${bi}`" class="next-block-label">{{ blockLabel(block, bi) }}</div>
                    <div v-for="(exercise, ei) in blockExercises(block)" :key="`bl-${bi}-ex-${ei}`" class="next-ex"
                        :class="blockClass(bi)">
                        <div class="next-ex-num">{{ bi + 1 }}</div>
                        <div class="next-ex-name">{{ exercise.name }}</div>
                        <div class="next-ex-detail">{{ exerciseDetail(exercise, block) }}</div>
                    </div>
                </template>
            </div>

            <button class="next-cta" :class="{ done: isDone }" @click="$emit('open', routine)">
                {{ ctaLabel }} →
            </button>
        </template>

        <div v-else class="next-empty">
            <v-icon size="40" color="txtMuted">mdi-calendar-blank-outline</v-icon>
            <div class="next-empty-title">{{ $t('common.home_no_training_today') }}</div>
            <div class="next-empty-sub">{{ $t('common.home_no_training_today_sub') }}</div>
        </div>
    </div>
</template>

<script>
export default {
    name: "TodayWorkoutCard",
    props: {
        routine: {
            type: Object,
            default: null,
        },
    },
    computed: {
        blocks(vm = this) {
            return vm.routine?.blocks || []
        },
        isDone(vm = this) {
            return !!vm.routine?.advance?.post
        },
        isStarted(vm = this) {
            if (vm.isDone) return false
            if (vm.routine?.advance?.pre) return true
            return vm.blocks.some(block => block.completed
                || (block.exercises || []).some(exercise => exercise.completed))
        },
        ctaLabel(vm = this) {
            if (vm.isDone) return vm.$t('common.home_see_summary')
            return vm.$t(vm.isStarted ? 'common.home_continue_training' : 'common.home_start_training')
        },
        tag(vm = this) {
            const objective = vm.routine?.description?.objective
            return objective ? vm.routineObjectiveLabel(objective) : ''
        },
        exercisesCount(vm = this) {
            let count = 0
            vm.blocks.forEach(block => {
                (block.exercises || []).forEach(ex => {
                    if (ex.type !== 'rest') count++
                })
            })
            return count
        },
        duration(vm = this) {
            let minutes = 0
            vm.blocks.forEach(block => {
                (block.exercises || []).forEach(ex => {
                    minutes += ex?.config?.sets?.length || block.sets || 0
                })
            })
            const added = Number(Number(minutes * 1.3).toFixed())
            return minutes === added ? `${minutes}` : `${minutes}-${added}`
        },
    },
    methods: {
        blockExercises(block) {
            return (block.exercises || []).filter(ex => ex.type !== 'rest')
        },
        blockClass(index) {
            return `block-${['a', 'b', 'c', 'd'][index % 4]}`
        },
        blockLabel(block, index) {
            const vm = this
            return vm.blockNameLabel(block.name) || `${vm.$tc('common.blocks', 1)} ${index + 1}`
        },
        exerciseDetail(exercise, block) {
            const vm = this
            if (block && block.isCircuit) {
                if (block.type === 'tabata') return vm.$t('circuits.tabata')
                const value = exercise?.config?.value
                const type = vm.$t(`exercises.short_${exercise?.config?.type || 'reps'}`)
                return `${block.sets || 1} × ${value != null ? value : ''} ${type}`.trim()
            }
            const sets = exercise?.config?.sets || []
            if (!sets.length) return ''
            const data = sets[0]?.data || []
            const primary = data.find(d => d?.short_name && d.short_name !== 'short_weight') || data[0]
            const typeKey = (primary?.short_name || 'short_reps').replace('short_', '')
            const typeLabel = vm.$te(`exercises.${typeKey}`) ? vm.$t(`exercises.${typeKey}`).toLowerCase() : ''
            let detail = `${sets.length}×${primary?.value != null ? primary.value : ''} ${typeLabel}`.trim()
            const weight = data.find(d => d?.short_name === 'short_weight')
            if (weight && Number(weight.value) > 0) detail += ` · ${weight.value}${vm.$t('exercises.short_weight')}`
            return detail
        },
    },
}
</script>

<style scoped>
.next-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.next-header {
    padding: 14px 18px 10px;
    flex-shrink: 0;
}

.next-tag {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--v-accent-base);
    background: rgba(51, 153, 153, 0.12);
    padding: 3px 8px;
    border-radius: 20px;
    display: inline-block;
}

.next-name {
    font-size: 16px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    margin-top: 4px;
}

.next-meta {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    margin-top: 2px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.next-meta span {
    display: flex;
    align-items: center;
    gap: 4px;
}

.next-meta .v-icon {
    color: var(--v-txtMuted-base) !important;
}

.next-exercises {
    padding: 0 14px 14px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    overflow-y: auto;
    max-height: 260px;
    flex: 1;
}

.next-block-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--v-txtMuted-base);
    padding: 6px 4px 2px;
}

.next-ex {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    background: var(--v-surface2-base);
    border-radius: 8px;
    min-width: 0;
}

.next-ex-num {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    font-weight: 800;
    color: #fff;
    flex-shrink: 0;
}

.block-a .next-ex-num {
    background: var(--v-accent-base);
}

.block-b .next-ex-num {
    background: var(--v-success-base);
}

.block-c .next-ex-num {
    background: #7C5CBF;
}

.block-d .next-ex-num {
    background: #D4782A;
}

.next-ex-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.next-ex-detail {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
    white-space: nowrap;
}

.next-cta {
    display: block;
    width: 100%;
    padding: 14px;
    background: var(--v-primary-base);
    color: #0C0E12;
    font-family: 'Outfit', sans-serif;
    font-size: 15px;
    font-weight: 700;
    text-align: center;
    border: none;
    cursor: pointer;
    border-radius: 0 0 14px 14px;
    transition: opacity 0.15s;
    flex-shrink: 0;
}

.next-cta:hover {
    opacity: 0.88;
}

.next-cta.done {
    background: var(--v-surface2-base);
    color: var(--v-txtPrimary-base);
}

.next-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 6px;
    padding: 40px 20px;
}

.next-empty-title {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    margin-top: 4px;
}

.next-empty-sub {
    font-size: 12px;
    color: var(--v-txtMuted-base);
}
</style>
